

import React, { useEffect, useRef, useState } from "react";
import { BsFilterLeft } from "react-icons/bs";
import {
  FiChevronDown,
  FiChevronRight,
  FiMessageSquare,
  FiRotateCcw,
} from "react-icons/fi";
import Video from "./Video";
import axios from "axios";
import cross from "../../assets/icons/cross.svg";
import { useTheme } from "../../context/ThemeProvider";
import { Link, useSearchParams } from "react-router-dom";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { useSupportChat } from "../../context/SupportChatContext";
import useAuth from "../../hooks/useAuth";
import { isCertifiedCreator as hasCertifiedCreatorAccess } from "../Creator/creatorAccess";
import {
  getSidebarFooterClass,
  getSidebarNavItemClass,
  getSidebarSecondaryActionClass,
  getSidebarSubItemClass,
  sidebarPrimaryActionClass,
} from "../../utils/sidebarStyles";

const HeroSection = ({ searchQuery, setSearchQuery }) => {
  const [filters, setFilters] = useState({});

  const { darkMode } = useTheme();
  const { auth } = useAuth();
  const isCertifiedCreator = hasCertifiedCreatorAccess(auth);
  const [searchParams, setSearchParams] = useSearchParams();

  const [filteredVideos, setFilteredVideos] = useState([]);
  const [totalPages, setTotalPages] = useState(1); // New state for total pages
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [durationRange, setDurationRange] = useState([0, 500]);
  const [selectedThemeFilters, setSelectedThemeFilters] = useState([]);
  const [selectedShotFilter, setSelectedShotFilter] = useState("all");
  const [sortBy, setSortBy] = useState("relevance");
  const [resolutionFilter, setResolutionFilter] = useState("all");
  const [orientationFilter, setOrientationFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(() => {
    const pageVal = parseInt(new URLSearchParams(window.location.search).get("page")) || 1;
    return pageVal;
  });
  const videosPerPage = 48;
  const { openChat } = useSupportChat();

  useEffect(() => {
    const handlePopState = () => {
      const pageFromUrl = parseInt(new URLSearchParams(window.location.search).get("page")) || 1;
      setCurrentPage(pageFromUrl);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const getData = async () => {
      try {
        const queryParams = new URLSearchParams();
        queryParams.set("page", currentPage);
        queryParams.set("limit", videosPerPage);
        selectedThemeFilters.forEach((theme) => {
          queryParams.append("theme", theme);
        });
        if (selectedShotFilter !== "all") {
          queryParams.set("shot", selectedShotFilter);
        }
        if (sortBy !== "relevance") {
          queryParams.set("sort", sortBy);
        }
        if (resolutionFilter !== "all") {
          queryParams.set("resolution", resolutionFilter);
        }
        if (orientationFilter !== "all") {
          queryParams.set("orientation", orientationFilter);
        }

        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/videos/get?${queryParams.toString()}`
        );
        console.log(response.data);
        setFilteredVideos(response.data.videos);
        setTotalPages(response.data.totalPages);
      } catch (error) {
        console.log("There was an error getting the videos!", error);
      }
    };

    getData();
  }, [
    currentPage,
    videosPerPage,
    selectedThemeFilters,
    selectedShotFilter,
    sortBy,
    resolutionFilter,
    orientationFilter,
  ]);

  const isFiltersFirstRender = useRef(true);

  useEffect(() => {
    if (isFiltersFirstRender.current) {
      isFiltersFirstRender.current = false;
      return;
    }
    setCurrentPage(1);
  }, [
    selectedThemeFilters,
    selectedShotFilter,
    sortBy,
    resolutionFilter,
    orientationFilter,
  ]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    setSearchParams({ page: currentPage }, { replace: true });
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage, setSearchParams]);

  const [activeSidebar, setActiveSidebar] = useState(null); // null | 'themes' | 'shotTypes' | etc.

  useEffect(() => {
    const fetchData = async () => {
      try {
        // setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/config/master-data`
        );
        // console.log(response?.data.data.oneTimePrices)
        // Delay state setting to ensure proper rendering
        // setOneTimePrices(response?.data?.data.oneTimePrices);
        // setSubscriptionPlans(response?.data?.data.subscriptionPlans );
        // console.log(response?.data.data.filters)
        setFilters(response?.data?.data.filters);

      } catch (err) {
        console.log(err);
        // Set default values if API fails
      }
    };

    fetchData();
  }, []);

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const generatePageNumbers = () => {
    const pages = [];
    if (currentPage > 1) pages.push(1); // First page
    if (currentPage > 2) pages.push(currentPage - 1); // Previous page
    pages.push(currentPage); // Current page
    if (currentPage < totalPages - 1) pages.push(currentPage + 1); // Next page
    if (currentPage < totalPages) pages.push(totalPages); // Last page
    return [...new Set(pages)]; // Remove duplicates
  };

  const allThemes = (filters?.themes || []).filter(Boolean);
  const allShots = (filters?.shots || []).filter(Boolean);
  const sidebarItems = [
    { id: "themes", label: "Video Themes", content: allThemes },
    { id: "shotTypes", label: "Shot Types", content: allShots },
    { id: "collections", label: "Collections", href: "/collections" },
  ];

  const setSidebarFilter = (sidebarId, value) => {
    if (sidebarId === "themes") {
      setSelectedThemeFilters((prev) =>
        prev.includes(value)
          ? prev.filter((selectedTheme) => selectedTheme !== value)
          : [...prev, value],
      );
    } else if (sidebarId === "shotTypes") {
      setSelectedShotFilter((prev) => (prev === value ? "all" : value));
    }
  };

  const isSidebarFilterSelected = (sidebarId, value) => {
    if (sidebarId === "themes") return selectedThemeFilters.includes(value);
    if (sidebarId === "shotTypes") return selectedShotFilter === value;
    return false;
  };

  const toggleTheme = (theme) => {
    setSelectedThemeFilters((prev) =>
      prev.includes(theme)
        ? prev.filter((selectedTheme) => selectedTheme !== theme)
        : [...prev, theme],
    );
  };

  const resetMetadataFilters = () => {
    setSelectedThemeFilters([]);
    setSelectedShotFilter("all");
    setSortBy("relevance");
    setResolutionFilter("all");
    setOrientationFilter("all");
    setDurationRange([0, 500]);
  };

  const visibleVideos = filteredVideos.filter((video) => {
    const videoDuration = parseFloat(video.duration);
    if (Number.isFinite(videoDuration)) {
      return (
        videoDuration >= durationRange[0] && videoDuration <= durationRange[1]
      );
    }
    return true;
  });

  const sidebarNavItemClass = (isActive = false) =>
    getSidebarNavItemClass(isActive, darkMode);
  const sidebarActionButtonClass = sidebarPrimaryActionClass;
  const sidebarSecondaryButtonClass = getSidebarSecondaryActionClass(darkMode);

  const renderFilterControls = () => (
    <div className="space-y-6 ">
      <div>
        <div className="text-sm font-medium mb-2">
          Duration: {durationRange[0]}s - {durationRange[1]}s
        </div>
        <Slider
          range
          min={0}
          max={500}
          value={durationRange}
          onChange={setDurationRange}
          trackStyle={{ backgroundColor: "var(--primary)" }}
          handleStyle={{
            borderColor: "var(--primary)",
            backgroundColor: "white",
            opacity: 1,
          }}
          railStyle={{
            backgroundColor: darkMode ? "#4b5563" : "#e5e7eb",
          }}
        />
      </div>

      {allThemes.length > 0 && (
        <div>
          <div className="text-sm font-medium mb-2">Themes</div>
          <div className="space-y-2">
            {allThemes.map((theme) => (
              <div key={theme} className="flex items-center">
                <button
                  type="button"
                  onClick={() => toggleTheme(theme)}
                  className={`w-4 h-4 rounded mr-2 border ${selectedThemeFilters.includes(theme)
                    ? "bg-[var(--primary)] border-[var(--primary)]"
                    : "border-gray-400"
                    }`}
                />
                <span className="text-sm">{theme}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="text-sm font-medium mb-2">Resolution</div>
        <div className="space-y-2">
          {["all", "4k", "1080p"].map((res) => (
            <div key={res} className="flex items-center">
              <button
                type="button"
                onClick={() => setResolutionFilter(res)}
                className={`w-4 h-4 rounded-full mr-2 border ${resolutionFilter === res
                  ? "bg-[var(--primary)] border-[var(--primary)]"
                  : "border-gray-400"
                  }`}
              />
              <span className="text-sm">
                {res === "all" ? "All Resolutions" : res}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="text-sm font-medium mb-2">Orientation</div>
        <div className="space-y-2">
          {["all", "horizontal", "vertical"].map((orientation) => (
            <div key={orientation} className="flex items-center">
              <button
                type="button"
                onClick={() => setOrientationFilter(orientation)}
                className={`w-4 h-4 rounded-full mr-2 border ${orientationFilter === orientation
                  ? "bg-[var(--primary)] border-[var(--primary)]"
                  : "border-gray-400"
                  }`}
              />
              <span className="text-sm">
                {orientation === "all" ? "All Orientations" : orientation}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="text-sm font-medium mb-2">Sort By</div>
        <div className="space-y-2">
          {["relevance", "views", "duration", "recent"].map((option) => (
            <div key={option} className="flex items-center">
              <button
                type="button"
                onClick={() => setSortBy(option)}
                className={`w-4 h-4 rounded-full mr-2 border ${sortBy === option
                  ? "bg-[var(--primary)] border-[var(--primary)]"
                  : "border-gray-400"
                  }`}
              />
              <span className="text-sm capitalize">
                {option === "recent" ? "Recent Uploaded" : option}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSidebarItems = ({ mobile = false } = {}) =>
    sidebarItems.map((item) => (
      <div key={item.id}>
        {item.href ? (
          <Link
            to={item.href}
            className={sidebarNavItemClass()}
            onClick={() => {
              if (mobile) setShowMobileFilters(false);
            }}
          >
            <span className="truncate">{item.label}</span>
            <FiChevronRight
              aria-hidden="true"
              className="shrink-0 text-base opacity-50 transition-opacity group-hover:opacity-100"
            />
          </Link>
        ) : (
          <>
            <button
              type="button"
              aria-expanded={activeSidebar === item.id}
              className={sidebarNavItemClass(activeSidebar === item.id)}
              onClick={() =>
                setActiveSidebar(activeSidebar === item.id ? null : item.id)
              }
            >
              <span className="truncate">{item.label}</span>
              <FiChevronDown
                aria-hidden="true"
                className={`shrink-0 text-base transition-transform duration-150 ${activeSidebar === item.id
                  ? "rotate-180 opacity-100"
                  : "opacity-50 group-hover:opacity-100"
                  }`}
              />
            </button>

            {item.id === activeSidebar && (
              <div className="flex flex-col gap-3 pb-2 pl-6 pr-3 pt-2">
                {item.content.map((contentItem) => (
                  <button
                    type="button"
                    key={contentItem}
                    onClick={() => setSidebarFilter(item.id, contentItem)}
                    className={getSidebarSubItemClass(
                      isSidebarFilterSelected(item.id, contentItem)
                    )}
                  >
                    {contentItem}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    ));

  const renderFilterPanel = ({ mobile = false } = {}) => (
    <div
      className={`${mobile ? showMobileFilters && "!flex" : ""
        } FiltersDiv flex-grow ${darkMode && "dark"}`}
      onClick={(e) => e.stopPropagation()}
    >
      {mobile && (
        <div
          className="ml-auto cursor-pointer h-[20px]"
          onClick={() => {
            setShowMobileFilters(false);
          }}
        >
          <img
            src={cross}
            alt="close"
            className={`h-full object-contain ${darkMode ? "" : "invert"}`}
          />
        </div>
      )}

      <>
        {/* <div
          className={`font-semibold cursor-pointer hover:text-[var(--primary)] ${activeSidebar === "filters" && "text-[var(--primary)]"
            } flex gap-2 items-center`}
          onClick={() =>
            setActiveSidebar(activeSidebar === "filters" ? null : "filters")
          }
        >
          <BsFilterLeft /> Filters & Sort
        </div>
        <button
          type="button"
          aria-label="Reset filters"
          title="Reset filters"
          onClick={resetMetadataFilters}
          className={`shrink-0 rounded-full border p-2 transition hover:border-[var(--primary)] hover:text-[var(--primary)] ${darkMode
            ? "border-[#3A3A3A] bg-[#20231F] text-white"
            : "border-[#CBCBCB] bg-white text-[var(--text)]"
            }`}
        >
          <FiRotateCcw className="text-base" />
        </button> */}
      </>

      {activeSidebar === "filters" && renderFilterControls()}

      <nav aria-label="Browse" className="-mx-3 flex flex-col gap-1">
        {renderSidebarItems({ mobile })}
      </nav>

      <div className={getSidebarFooterClass(darkMode)}>
        {mobile && (
          <button
            type="button"
            className={sidebarActionButtonClass}
            onClick={() => setShowMobileFilters(false)}
          >
            See Results
          </button>
        )}
        {!mobile && (
          <button
            type="button"
            onClick={() =>
              openChat({
                flow: "request_video",
                meta: { source: "homepage_sidebar" },
              })
            }
            className={sidebarActionButtonClass}
          >
            Request a video
          </button>
        )}
        {!mobile && !isCertifiedCreator && (
          <Link to="/creator" className={sidebarSecondaryButtonClass}>
            Become a Creator
          </Link>
        )}
        {!mobile && (
          <button
            type="button"
            onClick={() =>
              openChat({
                meta: { source: "homepage_sidebar_chat" },
              })
            }
            className={sidebarSecondaryButtonClass}
          >
            <FiMessageSquare />
            Chat
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="Home">
        <div
          className="mobileShowFiltersDivBtn text-[var(--text)] hover:text-[var(--primary)] font-semibold cursor-pointer mb-4 items-center gap-2 flex"
          onClick={() => {
            setActiveSidebar("filters");
            setShowMobileFilters(true);
          }}
        >
          <BsFilterLeft className="text-[var(--text)]" /> Filters & Sort
        </div>

        <div className="flex gap-1  relative scrollcontainer">
          {renderFilterPanel()}

          <div className="grid grid-cols-1 m-2  md:grid-cols-2 lg:grid-cols-3 gap-[0.4rem] w-full content-start">
            {visibleVideos?.length > 0 &&
              visibleVideos?.map((video, index) => (
                <div key={video._id} className="aspect-[16/9] border-none md:rounded-md overflow-hidden rounded-md"  >
                  {" "}
                  <Video video={video} priority={index < 6} />
                </div>
              ))}
          </div>
        </div>

        {/* Pagination
        <div className="mt-10 flex justify-center items-center gap-4">
          <div
            className={`text-2xl font-semibold cursor-pointer ${
              currentPage === 1 && "opacity-30 pointer-events-none"
            }`}
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          >
            ‹
          </div>

          {Array.from({ length: totalPages }, (_, i) => (
            <div
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`w-10 h-10 flex items-center justify-center rounded-full text-sm cursor-pointer 
                ${
                  currentPage === i + 1
                    ? "bg-[var(--primary)] text-black"
                    : "bg-transparent"
                }`}
            >
              {i + 1}
            </div>
          ))}

          <div
            className={`text-2xl font-semibold cursor-pointer ${
              currentPage === totalPages && "opacity-30 pointer-events-none"
            }`}
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
          >
            ›
          </div>
        </div> */}



        {/* Pagination */}
        <div className="mt-10 flex justify-center items-center gap-4">
          <div
            className={`text-2xl font-semibold cursor-pointer text-[var(--text)] ${currentPage === 1 && "opacity-30 pointer-events-none"
              }`}
            onClick={() => changePage(currentPage - 1)}
          >
            ‹
          </div>

          {generatePageNumbers().map((page, index, array) => (
            <React.Fragment key={page}>
              {index > 0 && page !== array[index - 1] + 1 && (
                <span className="mx-1 text-[var(--text)]">...</span>
              )}
              <div
                onClick={() => changePage(page)}
                className={`w-10 h-10 flex items-center justify-center rounded-full  text-sm cursor-pointer ${currentPage === page
                  ? "bg-[var(--primary)] text-black"
                  : "bg-transparent text-[var(--text)]"
                  }`}
              >
                {page}
              </div>
            </React.Fragment>
          ))}

          <div
            className={`text-2xl font-semibold cursor-pointer text-[var(--text)] ${currentPage === totalPages && "opacity-30 pointer-events-none"
              }`}
            onClick={() => changePage(currentPage + 1)}
          >
            ›
          </div>
        </div>
        {showMobileFilters && (
          <div
            className={`${darkMode ? "bg-[#121212CC]" : "bg-[#00000080]"} h-screen fixed inset-0 z-30 modalOverflow `}
            onClick={() => setShowMobileFilters(false)}
          >
            {renderFilterPanel({ mobile: true })}
          </div>
        )}
      </div>
    </>
  );
};

export default HeroSection;
